import type { APIResponse } from '@tabbio/types';
import { getAPIBaseURL } from './api-url';
import { appFetch } from './app-auth';

/**
 * The in-memory app access token, for the few public calls that make a raw
 * `fetch` but still tell the API who is asking. Never refreshes.
 */
export { getCurrentAccessToken } from './app-auth';

const API_URL = getAPIBaseURL();

type APIRequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  requiresAuth?: boolean;
  retryOnUnauthorized?: boolean;
};

function getHTTPErrorMessage(response: Response) {
  if (response.status === 413) {
    return 'Request is too large. Try a smaller file or shorter message.';
  }

  return response.statusText || `Request failed (${response.status})`;
}

export async function readAPIResponse<T>(
  response: Response,
): Promise<APIResponse<T>> {
  let parsed: APIResponse<T> | null = null;

  try {
    parsed = (await response.json()) as APIResponse<T>;
  } catch {
    return {
      data: null,
      error: {
        code: response.ok ? 'INVALID_JSON_RESPONSE' : `HTTP_${response.status}`,
        message: response.ok
          ? 'Server returned an invalid response.'
          : getHTTPErrorMessage(response),
        details: { status: response.status },
      },
      meta: {},
    };
  }

  if (!response.ok && !parsed?.error) {
    return {
      data: null,
      error: {
        code: `HTTP_${response.status}`,
        message: getHTTPErrorMessage(response),
        details: { status: response.status },
      },
      meta: parsed?.meta ?? {},
    };
  }

  return parsed;
}

function getNetworkErrorResponse<T>(error: unknown): APIResponse<T> {
  return {
    data: null,
    error: {
      code: 'NETWORK_ERROR',
      message: 'Network request failed. Check your connection and try again.',
      details: {
        originalMessage: error instanceof Error ? error.message : String(error),
      },
    },
    meta: {},
  };
}

async function requestJSON<T>(
  path: string,
  options: APIRequestOptions = {},
): Promise<APIResponse<T>> {
  const { body, requiresAuth, retryOnUnauthorized, headers: customHeaders, ...rest } =
    options;
  const headers = new Headers(customHeaders ?? {});

  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let response: Response;
  try {
    response = await appFetch(
      `${API_URL}${path}`,
      {
        ...rest,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      },
      { requiresAuth, retryOnUnauthorized },
    );
  } catch (error) {
    return getNetworkErrorResponse<T>(error);
  }

  return readAPIResponse<T>(response);
}

async function requestForm<T>(
  path: string,
  formData: FormData,
  options: APIRequestOptions = {},
): Promise<APIResponse<T>> {
  const { requiresAuth, retryOnUnauthorized, headers: customHeaders, ...rest } =
    options;
  let response: Response;
  try {
    response = await appFetch(
      `${API_URL}${path}`,
      {
        ...rest,
        method: rest.method ?? 'POST',
        body: formData,
        headers: customHeaders,
      },
      { requiresAuth, retryOnUnauthorized },
    );
  } catch (error) {
    return getNetworkErrorResponse<T>(error);
  }

  return readAPIResponse<T>(response);
}

export const api = {
  get: <T>(path: string, options?: APIRequestOptions) =>
    requestJSON<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: APIRequestOptions) =>
    requestJSON<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: APIRequestOptions) =>
    requestJSON<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: APIRequestOptions) =>
    requestJSON<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: APIRequestOptions) =>
    requestJSON<T>(path, { ...options, method: 'DELETE' }),
  postForm: <T>(path: string, formData: FormData, options?: APIRequestOptions) =>
    requestForm<T>(path, formData, { ...options, method: 'POST' }),
};
